/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { settingsStyles } from '../screens/settingsScreen.styles';
import { SoftPressable } from '../../shared/ui/SoftPressable';
import { ticketColors } from '../../shared/theme/ticketColors';

/** Square-ish warn amber for destructive rows (logout / close business). */
const ROW_WARN = '#B45309';

export type SettingsNavTone = 'default' | 'accent' | 'danger';

export type SettingsNavigationRowProps = {
  title: string;
  description: string;
  onPress: () => void;
  accessibilityLabel?: string;
  tone?: SettingsNavTone;
  /** Línea divisoria debajo de la fila (false en el último ítem). */
  showSeparator?: boolean;
  disabled?: boolean;
  /** Nombre de icono Ionicons para el icon-wrap (clean design mobile). */
  iconName?: React.ComponentProps<typeof Ionicons>['name'];
  /** testID para E2E (se propaga al TouchableOpacity). */
  testID?: string;
};

export function SettingsNavigationRow({
  title,
  description,
  onPress,
  accessibilityLabel,
  tone = 'default',
  showSeparator = true,
  disabled = false,
  iconName,
  testID,
}: SettingsNavigationRowProps) {
  const titleColor =
    tone === 'danger' ? ROW_WARN : tone === 'accent' ? ticketColors.brandInk : ticketColors.ink;
  const chevronColor =
    tone === 'danger' ? ROW_WARN : tone === 'accent' ? ticketColors.gold : ticketColors.ink2;
  const iconWrapBg =
    tone === 'danger'
      ? ticketColors.warnBg
      : tone === 'accent'
        ? ticketColors.brandSoft
        : ticketColors.paper2;
  const iconWrapBorder =
    tone === 'danger'
      ? ticketColors.warnBorder
      : tone === 'accent'
        ? ticketColors.gold
        : ticketColors.ruleSoft;
  const iconColor =
    tone === 'danger' ? ROW_WARN : tone === 'accent' ? ticketColors.gold : ticketColors.ink2;

  return (
    <View>
      <SoftPressable
        style={[settingsStyles.navRow, disabled && settingsStyles.navRowDisabled]}
        onPress={onPress}
        disabled={disabled}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel ?? `${title}. ${description}`}
        accessibilityState={{ disabled }}
        testID={testID}
      >
        {iconName ? (
          <View style={[rowStyles.iconWrap, { backgroundColor: iconWrapBg, borderColor: iconWrapBorder }]}>
            <Ionicons name={iconName} size={16} color={iconColor} />
          </View>
        ) : null}
        <View style={settingsStyles.navRowBody}>
          <Text style={[settingsStyles.navRowTitle, { color: titleColor }]} numberOfLines={2}>
            {title}
          </Text>
          <Text style={settingsStyles.navRowDescription} numberOfLines={4}>
            {description}
          </Text>
        </View>
        <Ionicons name="chevron-forward" size={20} color={chevronColor} />
      </SoftPressable>
      {showSeparator ? <View style={settingsStyles.navRowSeparator} /> : null}
    </View>
  );
}

const rowStyles = StyleSheet.create({
  iconWrap: {
    width: 36,
    height: 36,
    borderRadius: 10,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
});

